/**
 * The pure view-model behind every link the console hands to another device —
 * a table's TV screen, a guest's join page: which URL a TV or phone on the
 * wifi can actually open, the one-line how-to for the device the owner is
 * holding, and whether the browser's in-page cast can be pointed at that URL.
 * No React, no fetching — the caller hands over the origin, the path, the
 * `/api/config` answer, and the platform.
 */

import type { ApplePlatform } from '../lib/platform.js';

export const LOCAL_HOST = /localhost|127\.0\.0\.1|\[::1\]/;

export interface TvConfig {
  publicUrl?: string;
  lanUrls?: string[];
  /** `CAST_APP_ID` from the server, when native Google Cast is configured. */
  castAppId?: string | null;
}

export interface TvLinkInput {
  origin: string;
  /** `/tv?table=CODE`, `/play?casino=CODE` — the path the other device opens. */
  tvPath: string;
  /** `/api/config` answer (LAN URLs on localhost, plus the Cast App ID). */
  config: TvConfig | null;
  /**
   * The owner is on an iPhone/iPad (`ios`) or Safari on a Mac (`mac`): in-page
   * casting is off the table, and the way to the TV is Apple's own mirroring.
   */
  apple: ApplePlatform;
}

export interface TvLinkVM {
  /** The URL to open on the TV, or on the phone that will mirror it. */
  openUrl: string;
  /** The same URL with the scheme stripped, for typing on a TV remote. */
  openUrlShort: string;
  /** True when the console's own origin is a dead end for other devices. */
  local: boolean;
  /** The one-line how-to for getting this URL onto the TV. */
  guidance: string;
  /** The URL a cast receiver would load itself, when that can work. */
  castUrl: string | null;
  /** Why in-page casting is out, when it is. */
  castBlocked: string | null;
}

const reachable = (url: string | undefined): string | null =>
  url && !LOCAL_HOST.test(url) ? url : null;

/** The one-line how-to for getting a TV URL onto the screen in the room. */
export function tvGuidance(apple: ApplePlatform): string {
  if (apple === 'ios') {
    return 'Open the table link in Safari, then Control Center → Screen Mirroring → your TV. Keep the phone unlocked.';
  }
  if (apple === 'mac') {
    return 'Apple TV: open the table link, then Control Center → Screen Mirroring. Chromecast: Chrome menu → Cast → Cast tab.';
  }
  return 'Open the table link in the TV’s browser, or Chrome menu → Cast → Cast tab.';
}

export function tvLinkModel(input: TvLinkInput): TvLinkVM {
  const local = LOCAL_HOST.test(input.origin);
  const lan = (input.config?.lanUrls ?? [])
    .map(reachable)
    .find((u): u is string => u !== null) ?? null;
  const configured = reachable(input.config?.publicUrl);

  // A localhost console is useless to every other device in the room, and the
  // server names the addresses that do work. Any other origin speaks for itself.
  const base = local ? ((lan ?? configured) ?? input.origin) : input.origin;
  const openUrl = `${base}${input.tvPath}`;

  const castUrl = reachable(openUrl);
  const castBlocked = castUrl
    ? null
    : 'Cast devices can’t reach localhost. Run `pnpm tunnel`, or use Chrome menu → Cast → Cast tab.';

  return {
    openUrl,
    openUrlShort: openUrl.replace(/^https?:\/\//, ''),
    local,
    guidance: tvGuidance(input.apple),
    castUrl,
    castBlocked,
  };
}
